import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import { Modal, Button, Form } from 'semantic-ui-react';
import { TargetSelect } from '@code-yellow/spider';
import { DeveloperAllocation, DeveloperAllocationEntry } from '../../store/planning/DeveloperAllocation';
import { User, UserStore } from '../../store/User';
import { Project, ProjectStore } from '../../store/Project';
import { api } from '../../store/Base';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];
function projectColor(name: string): string {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function getProjectName(project: any): string {
    if (!project) return '';
    if (typeof project === 'object') return project.name || `#${project.id}`;
    return `#${project}`;
}

function hexTint(hex: string, alpha: number): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return hex;
    const int = parseInt(m[1], 16);
    const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface ProjectChip {
    project: Project;
    reason: 'milestone' | 'recent';
}

@observer
export default class AllocationEditModal extends React.Component<any, any> {
    userStore = new UserStore();
    projectStore = new ProjectStore({ limit: false });

    @observable allocationModel: DeveloperAllocation = new DeveloperAllocation({}, {
        relations: ['entries.project', 'user'],
    });
    @observable saving = false;
    // [T54193] Week-based editing state
    @observable weekOffset = 0;            // 0 = week containing the allocation's start
    @observable pendingScope: 'following' | 'week' = 'following';
    @observable searchEntry: DeveloperAllocationEntry = new DeveloperAllocationEntry({}, { relations: ['project'] });
    @observable dragProjectId: number | null = null;

    componentDidMount() {
        this.populateFromModel();
        // Fetch all projects so the likely-projects chips can render
        this.projectStore.fetch().catch((e: any) => console.warn('project fetch failed in alloc modal', e));
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.model?.id !== this.props.model?.id && this.props.open) {
            this.populateFromModel();
        }
    }

    get isCreating(): boolean {
        const m = this.props.model as DeveloperAllocation;
        return !m || !m.id;
    }

    get entriesByDay(): Map<number, DeveloperAllocationEntry[]> {
        const map = new Map<number, DeveloperAllocationEntry[]>();
        const entries = (this.allocationModel.entries as any)?.models || [];
        for (const e of entries as DeveloperAllocationEntry[]) {
            const dow = (e as any).dayOfWeek ?? 0;
            if (!map.has(dow)) map.set(dow, []);
            const arr = map.get(dow); if (arr) arr.push(e);
        }
        return map;
    }

    /** Projects the user is likely to want: the milestone's project first
     * (when provided), then every project the dev is already allocated to. */
    get likelyProjects(): ProjectChip[] {
        const seen = new Set<number>();
        const out: ProjectChip[] = [];
        const add = (project: any, reason: ProjectChip['reason']) => {
            const id = Number((project as any)?.id ?? project ?? 0);
            if (!id || seen.has(id)) return;
            seen.add(id);
            out.push({ project, reason });
        };
        // Milestone's project (passed via props) first
        const ctxProjectId = Number(this.props.projectId);
        const all = (this.projectStore.models as Project[] || []);
        if (ctxProjectId) {
            const found = all.find(p => Number(p.id) === ctxProjectId);
            if (found) add(found, 'milestone');
        }
        // Dev's current projects across ALL their allocations
        const m = this.props.model as DeveloperAllocation;
        const userId = this.isCreating
            ? Number((this.allocationModel.user as any)?.id ?? 0)
            : Number((m as any)?.user?.id ?? (m as any)?.user ?? 0);
        if (userId) {
            const allocs = (this.props.allocations as DeveloperAllocation[]) || [];
            for (const a of allocs) {
                const aUser = Number((a as any).user?.id ?? (a as any).user ?? 0);
                if (aUser !== userId) continue;
                const entries = ((a as any).entries?.models || (a as any).entries || []) as any[];
                for (const e of entries) {
                    const proj = (e as any).project;
                    if (proj) add(proj, 'recent');
                }
            }
        }
        return out;
    }

    /** [T54193] All of this dev's allocations (from parent store). */
    get devAllocations(): DeveloperAllocation[] {
        const m = this.props.model as DeveloperAllocation;
        const userId = Number((m as any)?.user?.id ?? (m as any)?.user ?? 0);
        if (!userId) return [];
        const allocs = (this.props.allocations as DeveloperAllocation[]) || [];
        return allocs.filter(a => Number((a as any).user?.id ?? (a as any).user ?? 0) === userId);
    }

    /** Monday of the currently visible week. */
    get currentWeekMonday(): Date {
        const m = this.props.model as DeveloperAllocation;
        const base = (m as any)?.startDate ? moment((m as any).startDate).toDate() : new Date();
        const monday0 = new Date(base);
        monday0.setHours(0, 0, 0, 0);
        monday0.setDate(monday0.getDate() - monday0.getDay() + (monday0.getDay() === 0 ? -6 : 1));
        const d = new Date(monday0);
        d.setDate(d.getDate() + this.weekOffset * 7);
        return d;
    }

    /** The allocation active on the visible week's Monday (per-date
     * resolution — a dev can have multiple consecutive schedules). Week 0
     * anchors to THIS modal's allocation's start week, so the pattern being
     * edited is always the one shown even when it starts mid-week. */
    get activeAllocationForWeek(): DeveloperAllocation | null {
        const monday = this.currentWeekMonday;
        const iso = moment(monday).format('YYYY-MM-DD');
        // The allocation whose range covers ANY day of the visible week —
        // an allocation starting mid-week (e.g. created today, Wednesday)
        // must still show as "this week's" pattern.
        const weekEnd = moment(monday).add(6, 'days').format('YYYY-MM-DD');
        const active = this.devAllocations.filter(a => {
            const s = (a as any).startDate ? moment((a as any).startDate).format('YYYY-MM-DD') : null;
            const e = (a as any).endDate ? moment((a as any).endDate).format('YYYY-MM-DD') : null;
            if (!s) return false;
            const startsWithinWeek = s >= iso && s <= weekEnd;
            const coversWeek = s <= iso && (!e || e >= iso);
            return startsWithinWeek || coversWeek;
        });
        if (active.length === 0) return null;
        // Prefer the one that covers the whole week; else the latest starter.
        const covering = active.find(a => {
            const s = moment((a as any).startDate).format('YYYY-MM-DD');
            return s <= iso;
        });
        return covering || active[active.length - 1];
    }

    /** Stored pattern for the visible week (null when nothing stored). */
    get storedPattern(): { dayOfWeek: number; projectId: number; hours: number }[] | null {
        const alloc = this.activeAllocationForWeek;
        if (!alloc) return null;
        const entries = ((alloc as any).entries?.models || (alloc as any).entries || []) as any[];
        return entries.map(e => ({
            dayOfWeek: Number(e.dayOfWeek ?? 0),
            projectId: Number((e.project as any)?.id ?? e.project ?? 0),
            hours: Number(e.hours ?? 0),
        }));
    }

    /** The pattern currently edited in the calendar. */
    get editedPattern(): { dayOfWeek: number; projectId: number; hours: number }[] {
        const entries = ((this.allocationModel.entries as any)?.models || []) as any[];
        return entries.filter(e => e.project).map(e => ({
            dayOfWeek: Number(e.dayOfWeek ?? 0),
            projectId: Number((e.project as any)?.id ?? e.project ?? 0),
            hours: Number(e.hours ?? 0),
        }));
    }

    get patternChanged(): boolean {
        const sig = (p: { dayOfWeek: number; projectId: number; hours: number }[] | null) =>
            p === null ? 'none' : JSON.stringify([...p].sort((a, b) =>
                a.dayOfWeek - b.dayOfWeek || a.projectId - b.projectId || a.hours - b.hours));
        return sig(this.editedPattern) !== sig(this.storedPattern);
    }

    @action populateFromModel = () => {
        const m = this.props.model as DeveloperAllocation;
        if (!m || !m.id) {
            this.allocationModel = new DeveloperAllocation({}, {
                relations: ['entries.project', 'user'],
            });
            return;
        }
        this.allocationModel = new DeveloperAllocation({
            id: m.id,
            user: (m as any).user?.toJS ? (m as any).user.toJS() : (m as any).user,
            startDate: (m as any).startDate,
            endDate: (m as any).endDate,
        }, {
            relations: ['entries.project', 'user'],
        });
        // [T54193] Week-based editing: show the pattern effective in the
        // FIRST week of this allocation (weekOffset 0); navigating weeks
        // reloads the pattern active in that week.
        this.weekOffset = 0;
        this.loadWeekPattern();
    };

    /** Load the stored pattern for the currently visible week into the
     * editable calendar. [T54193] */
    @action loadWeekPattern = () => {
        const target = this.isCreating ? null : this.activeAllocationForWeek;
        // Rebuild a fresh scratch model with the target week's entries
        const m = this.props.model as DeveloperAllocation;
        const scratch = new DeveloperAllocation({
            id: m?.id,
            user: (m as any)?.user?.toJS ? (m as any).user.toJS() : (m as any)?.user,
            startDate: (m as any)?.startDate,
            endDate: (m as any)?.endDate,
        }, {
            relations: ['entries.project', 'user'],
        });
        if (target) {
            const entries = ((target as any).entries?.models || (target as any).entries || []) as any[];
            for (const e of entries) {
                const entryData: Record<string, any> = {
                    dayOfWeek: e.dayOfWeek ?? 0,
                    hours: e.hours ?? 8,
                };
                const proj = (e as any).project;
                if (proj?.toJS) {
                    entryData.project = proj.toJS();
                } else if (proj !== undefined) {
                    entryData.project = proj;
                }
                (scratch.entries as any).add(entryData);
            }
        }
        this.allocationModel = scratch;
    };

    @action addProjectToDay = (dayOfWeek: number, project: any) => {
        const projData = (project as any)?.toJS ? (project as any).toJS() : project;
        (this.allocationModel.entries as any).add({
            dayOfWeek,
            hours: 8,
            project: projData,
        });
    };

    @action removeEntry = (entry: DeveloperAllocationEntry) => {
        (this.allocationModel.entries as any).remove(entry);
    };

    @action setEntryHours = (entry: DeveloperAllocationEntry, hours: string) => {
        (entry as any).hours = parseFloat(hours) || 0;
    };

    @action handleDrop = (dayOfWeek: number, e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const raw = e.dataTransfer.getData('text/plain');
        if (!raw) return;
        const projectId = parseInt(raw);
        if (!projectId) return;
        const proj = (this.projectStore.models as Project[] || []).find(p => Number(p.id) === projectId);
        this.addProjectToDay(dayOfWeek, proj || projectId);
        this.dragProjectId = null;
    };

    @action navigateWeek = (delta: number) => {
        this.weekOffset += delta;
        if (!this.isCreating) {
            this.loadWeekPattern();
        }
    };

    @action setScope = (scope: 'following' | 'week') => { this.pendingScope = scope; };

    @action save = async () => {
        this.saving = true;
        try {
            const entries = (this.allocationModel.entries as any)?.models || [];

            // [T54193] Week-based editing: an EXISTING schedule is revised
            // through set_schedule (split/merge semantics — "this week" or
            // "this and following"). Only first-time creation uses the plain
            // POST below.
            const model = this.props.model as DeveloperAllocation;
            if (!this.isCreating && model?.id) {
                const monday = this.currentWeekMonday;
                const setPayload = {
                    user: Number((model as any).user?.id ?? (model as any).user),
                    week: moment(monday).format('YYYY-MM-DD'),
                    scope: this.pendingScope,
                    entries: entries.filter((e: any) => e.project).map((e: any) => ({
                        day_of_week: Number(e.dayOfWeek ?? 0),
                        project: Number((e.project as any)?.id ?? e.project),
                        hours: Number(e.hours ?? 8),
                    })),
                };
                if (setPayload.entries.length === 0 && this.storedPattern !== null && this.storedPattern.length > 0) {
                    // Clearing the whole week = removing that week's schedule
                    // is NOT supported via set_schedule; treat as no-op save.
                    alert('Remove entries via "Remove allocation" or drag projects to empty days.');
                    this.saving = false;
                    return;
                }
                await api.post('developer_allocation/set_schedule/', setPayload);
                if (this.props.afterSave) {
                    await this.props.afterSave();
                }
                if (this.props.onClose) {
                    this.props.onClose();
                }
                return;
            }

            const payload: Record<string, any> = {
                start_date: this.allocationModel.startDate
                    ? moment(this.allocationModel.startDate).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                end_date: this.allocationModel.endDate
                    ? moment(this.allocationModel.endDate).format('YYYY-MM-DD')
                    : null,
            };

            if (this.isCreating) {
                if (!this.allocationModel.user) {
                    alert('Please select a developer.');
                    this.saving = false;
                    return;
                }
                payload.user = (this.allocationModel.user as any)?.id ?? this.allocationModel.user;
            }

            const validEntries = entries.filter((e: any) => e.project);
            if (validEntries.length > 0) {
                payload.entries = validEntries.map((e: any) => {
                    const entry: Record<string, any> = {
                        day_of_week: e.dayOfWeek,
                        project: e.project?.id ?? e.project,
                        hours: e.hours ?? 8,
                    };
                    if (e.id) entry.id = e.id;
                    return entry;
                });
            }

            const url = this.isCreating
                ? 'developer_allocation/'
                : 'developer_allocation/' + this.allocationModel.id + '/';

            if (this.isCreating) {
                await api.post(url, payload);
            } else {
                await api.patch(url, payload);
            }

            if (this.props.afterSave) {
                await this.props.afterSave();
            }
            if (this.props.onClose) {
                this.props.onClose();
            }
        } catch (e: any) {
            console.error('AllocationEditModal save error:', e);
            alert('Failed to save allocation: ' + (e?.message || 'unknown error'));
        } finally {
            this.saving = false;
        }
    };

    @action remove = async () => {
        if (!this.allocationModel.id) return;
        if (!window.confirm('Remove this allocation?')) return;
        this.saving = true;
        try {
            await api.delete('developer_allocation/' + this.allocationModel.id + '/');
            if (this.props.afterSave) {
                await this.props.afterSave();
            }
            if (this.props.onClose) {
                this.props.onClose();
            }
        } catch (e: any) {
            console.error('AllocationEditModal delete error:', e);
            alert('Failed to remove allocation: ' + (e?.message || 'unknown error'));
        } finally {
            this.saving = false;
        }
    };

    render() {
        const { open, onClose } = this.props;
        const isCreating = this.isCreating;
        const chips = this.likelyProjects;
        const searchProjRaw = (this.searchEntry as any).project;
        // TargetSelect materializes an empty Project model (id=null) on init —
        // only treat it as picked when it has a real id.
        const searchProj = Number((searchProjRaw as any)?.id ?? 0) ? searchProjRaw : null;

        return (
            <Modal
                open={open}
                onClose={onClose}
                size="large"
                closeOnDimmerClick={false}
                style={{ height: 'fit-content', maxHeight: 'calc(100vh - 32px)' }}
            >
                <Modal.Header>
                    {isCreating ? 'Add Developer Allocation' : 'Edit Allocation'}
                </Modal.Header>
                {/* Content scrolls internally so header + actions stay
                    pinned on screen at any viewport height. calc: 100vh
                    minus modal margins (64px) minus header (65px) and
                    actions (65px) rows. */}
                <Modal.Content style={{ maxHeight: 'calc(100vh - 194px)', overflowY: 'auto' }}>
                    <Form>
                        {/* Developer selector */}
                        <Form.Field>
                            <label>Developer</label>
                            <div data-test-alloc-user-select>
                                <TargetSelect
                                    remote
                                    search
                                    selection
                                    clearable
                                    fluid
                                    noLabel
                                    skipFetch
                                    name="user"
                                    target={this.allocationModel}
                                    store={this.userStore}
                                    toOption={(user: User) => ({
                                        value: user.id,
                                        text: (user as any).fullName || `${(user as any).firstName || ''} ${(user as any).lastName || ''}`.trim() || (user as any).email,
                                    })}
                                    placeholder="Search developer..."
                                    disabled={!isCreating}
                                />
                            </div>
                        </Form.Field>

                        {/* Date range — only for first-time creation; week-based
                            editing (set_schedule) manages ranges automatically. */}
                        {isCreating && (
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Start Date</label>
                                <input type="date" data-test-alloc-start-date
                                    value={this.allocationModel.startDate ? moment(this.allocationModel.startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}
                                    onChange={(e) => { (this.allocationModel as any).startDate = e.target.value; }}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>End Date (empty = open-ended)</label>
                                <input type="date" data-test-alloc-end-date
                                    value={this.allocationModel.endDate ? moment(this.allocationModel.endDate).format('YYYY-MM-DD') : ''}
                                    onChange={(e) => { (this.allocationModel as any).endDate = e.target.value || null; }}
                                />
                            </Form.Field>
                        </Form.Group>
                        )}

                        {/* ── [T54193] Week navigator ── */}
                        {!isCreating && (
                            <Form.Field>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Button basic size="small" icon="chevron left" data-test-alloc-week-prev
                                        onClick={() => this.navigateWeek(-1)} />
                                    <div data-test-alloc-week-label style={{ fontWeight: 600, minWidth: 170, textAlign: 'center' }}>
                                        Week of {moment(this.currentWeekMonday).format('MMM D, YYYY')}
                                    </div>
                                    <Button basic size="small" icon="chevron right" data-test-alloc-week-next
                                        onClick={() => this.navigateWeek(1)} />
                                    {this.weekOffset !== 0 && (
                                        <Button basic size="small" data-test-alloc-week-reset onClick={() => { this.weekOffset = 0; this.loadWeekPattern(); }}>
                                            Back to start
                                        </Button>
                                    )}
                                </div>
                            </Form.Field>
                        )}

                        {/* ── Calendar view: week grid with drag-and-drop ── */}
                        <Form.Field>
                            <label>Weekly Schedule — drag a project onto a day</label>
                            <div data-test-alloc-calendar style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                {/* Left: likely projects + search */}
                                <div data-test-alloc-projects-panel style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ fontSize: 11, color: '#5c6470', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                        Projects
                                    </div>
                                    <div data-test-alloc-quickfill-project>
                                        <TargetSelect
                                            remote
                                            search
                                            selection
                                            clearable
                                            fluid
                                            noLabel
                                            skipFetch
                                            name="project"
                                            target={this.searchEntry}
                                            store={this.projectStore}
                                            toOption={(project: Project) => ({
                                                value: project.id,
                                                text: (project as any).name || `Project #${project.id}`,
                                            })}
                                            placeholder="Search project to add…"
                                        />
                                    </div>
                                    {searchProj && (
                                        <div
                                            data-test-alloc-chip={`search-${Number((searchProj as any).id ?? searchProj)}`}
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData('text/plain', String((searchProj as any).id ?? searchProj));
                                                this.dragProjectId = Number((searchProj as any).id ?? searchProj);
                                            }}
                                            onDragEnd={() => { this.dragProjectId = null; }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 6,
                                                padding: '6px 8px',
                                                borderRadius: 6,
                                                border: '1px solid #e2e5e9',
                                                background: '#fff',
                                                cursor: 'grab',
                                                fontSize: 12,
                                            }}
                                        >
                                            <span style={{ width: 10, height: 10, borderRadius: 2, background: projectColor(getProjectName(searchProj)), flexShrink: 0 }} />
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getProjectName(searchProj)}</span>
                                            <span style={{ fontSize: 10, color: '#9099a8' }}>drag me</span>
                                        </div>
                                    )}
                                    {chips.map(({ project, reason }) => {
                                        const name = getProjectName(project);
                                        const color = projectColor(name);
                                        const pid = Number((project as any)?.id ?? project);
                                        return (
                                            <div
                                                key={pid}
                                                data-test-alloc-chip={pid}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData('text/plain', String(pid));
                                                    this.dragProjectId = pid;
                                                }}
                                                onDragEnd={() => { this.dragProjectId = null; }}
                                                title={reason === 'milestone' ? 'Milestone project' : 'Already allocated to this developer'}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                    padding: '6px 8px',
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e5e9',
                                                    background: this.dragProjectId === pid ? hexTint(color, 0.15) : '#fff',
                                                    cursor: 'grab',
                                                    fontSize: 12,
                                                }}
                                            >
                                                <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
                                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                                {reason === 'milestone' && <span style={{ fontSize: 9, color: '#2185d0', fontWeight: 600 }}>★</span>}
                                                {reason === 'recent' && <span style={{ fontSize: 9, color: '#9099a8' }}>↺</span>}
                                            </div>
                                        );
                                    })}
                                    {chips.length === 0 && !searchProj && (
                                        <span style={{ fontSize: 12, color: '#c0c5cc', padding: '8px 0' }}>
                                            Search a project to get started
                                        </span>
                                    )}
                                </div>

                                {/* Right: week calendar */}
                                <div data-test-alloc-week style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                                    {DAY_NAMES.map((dayName, dayIdx) => {
                                        const dayEntries = this.entriesByDay.get(dayIdx) || [];
                                        const totalHours = dayEntries.reduce((sum, e) => sum + (Number((e as any).hours) || 0), 0);
                                        const isWeekend = dayIdx >= 5;
                                        return (
                                            <div
                                                key={dayIdx}
                                                data-test-alloc-day={dayIdx}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onDrop={(e) => this.handleDrop(dayIdx, e)}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', gap: 4,
                                                    minHeight: 160,
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e5e9',
                                                    background: isWeekend ? '#fafbfc' : '#fff',
                                                    padding: 6,
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #f0f2f5', paddingBottom: 4 }}>
                                                    <strong style={{ fontSize: 12 }}>{DAY_SHORT[dayIdx]}</strong>
                                                    <span style={{ fontSize: 11, color: totalHours > 0 ? '#2185d0' : '#9099a8' }}>{totalHours}h</span>
                                                </div>
                                                {dayEntries.map((entry: DeveloperAllocationEntry, eIdx: number) => {
                                                    const name = (entry as any).project ? getProjectName((entry as any).project) : '';
                                                    const color = (entry as any).project ? projectColor(name) : '#e2e5e9';
                                                    return (
                                                        <div
                                                            key={eIdx}
                                                            data-test-alloc-entry={eIdx}
                                                            style={{
                                                                display: 'flex', flexDirection: 'column', gap: 2,
                                                                borderRadius: 4,
                                                                background: hexTint(color, 0.10),
                                                                border: `1px solid ${hexTint(color, 0.30)}`,
                                                                padding: '4px 6px',
                                                                fontSize: 11,
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                                                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{name || 'No project'}</span>
                                                                <button
                                                                    type="button"
                                                                    data-test-alloc-entry-remove={eIdx}
                                                                    onClick={() => this.removeEntry(entry)}
                                                                    style={{ padding: 0, border: 'none', background: 'transparent', color: '#db2828', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <input
                                                                    type="number"
                                                                    data-test-alloc-entry-hours={eIdx}
                                                                    min={0}
                                                                    max={24}
                                                                    step={0.5}
                                                                    value={isNaN(Number((entry as any).hours)) ? 8 : Number((entry as any).hours)}
                                                                    onChange={(e) => this.setEntryHours(entry, e.target.value)}
                                                                    style={{ width: 44, padding: '2px 4px', border: '1px solid #e2e5e9', borderRadius: 3, fontSize: 11 }}
                                                                />
                                                                <span style={{ fontSize: 10, color: '#9099a8' }}>h</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {dayEntries.length === 0 && (
                                                    <span style={{ fontSize: 11, color: '#c0c5cc', textAlign: 'center', marginTop: 'auto', marginBottom: 'auto' }}>
                                                        {isWeekend ? '—' : 'drop here'}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Form.Field>

                        {/* ── [T54193] Scope picker: how far does this change apply? ── */}
                        {!isCreating && (
                            <Form.Field>
                                <label>Apply changes to</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button size="small" data-test-alloc-scope-following
                                        primary={this.pendingScope === 'following'}
                                        onClick={() => this.setScope('following')}>
                                        This and following weeks
                                    </Button>
                                    <Button size="small" data-test-alloc-scope-week
                                        primary={this.pendingScope === 'week'}
                                        onClick={() => this.setScope('week')}>
                                        This week only
                                    </Button>
                                </div>
                                {this.patternChanged ? (
                                    <div data-test-alloc-scope-hint style={{ marginTop: 4, fontSize: 11, color: '#5c6470' }}>
                                        {this.pendingScope === 'following'
                                            ? 'The new schedule applies from this week until a week where something else was planned.'
                                            : 'Only this week changes; the current schedule resumes afterwards.'}
                                    </div>
                                ) : (
                                    <div data-test-alloc-scope-hint style={{ marginTop: 4, fontSize: 11, color: '#5c6470' }}>
                                        No changes yet — editing days above will schedule from this week.
                                    </div>
                                )}
                            </Form.Field>
                        )}
                        {isCreating && (
                            <div style={{ marginTop: 8, padding: 8, background: '#f5f6f7', borderRadius: 4, fontSize: 12, color: '#5c6470' }}>
                                <strong>Note:</strong> Leave end date empty for open-ended allocation. The schedule applies from the start date; change it later per week from the timeline.
                            </div>
                        )}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {!isCreating && (
                        <Button color="red" onClick={this.remove} disabled={this.saving} style={{ marginRight: 'auto' }} data-test-alloc-remove>
                            Remove
                        </Button>
                    )}
                    <Button onClick={onClose} disabled={this.saving}>Cancel</Button>
                    <Button color="blue" onClick={this.save} loading={this.saving} data-test-alloc-save>
                        {isCreating ? 'Add Allocation' : 'Save'}
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }
}
